const HEALTH_COLORS = {
  Healthy: "#008b2e",
  Warning: "#f59e0b",
  Critical: "#ef4444",
  Inactive: "#94a3b8",
};

const UTILIZATION_COLORS = {
  "High (80 - 100%)": "#008b2e",
  "Medium (50 - 80%)": "#2f6bea",
  "Low (20 - 50%)": "#94a3b8",
  "Idle (< 20%)": "#e5e7eb",
};

const getRows = (state) => {
  const payload = state?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getHaltDevices = (state) => {
  const payload = state?.data;
  return Array.isArray(payload?.devices) ? payload.devices : [];
};

const toNumber = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

const parseHour = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    if (value >= 0 && value <= 23) return Math.floor(value);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.getHours();
  }

  const text = String(value);
  const timeMatch = text.match(/(?:^|[T,\s])(\d{1,2}):(\d{2})/);
  if (timeMatch) return Math.min(23, parseInt(timeMatch[1], 10));

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date.getHours();
};

const isOnTimeTrip = (status) => String(status || "") === "Completed";

const isDelayedTrip = (status) => {
  const value = String(status || "");
  return value.includes("Not Completed") || value.includes("over");
};

const getVehicleStatus = (vehicle) =>
  String(vehicle?.status || vehicle?.Status || "").toUpperCase();

const getExpectedHours = (selectedDateRange, customDateRange) => {
  if (selectedDateRange === "custom" && customDateRange?.startDate && customDateRange?.endDate) {
    const start = new Date(customDateRange.startDate);
    const end = new Date(customDateRange.endDate);
    const hours = (end.getTime() - start.getTime()) / 3600000;
    return Math.max(hours, 1);
  }

  switch (selectedDateRange) {
    case "yesterday":
      return 24;
    case "last 7 days":
    case "last_7_days":
    case "this_week":
      return 7 * 24;
    case "last_30_days":
    case "this_month":
      return 30 * 24;
    case "today":
    default: {
      const now = new Date();
      return Math.max(now.getHours() + now.getMinutes() / 60, 1);
    }
  }
};

const percent = (part, total) => {
  if (!total) return 0;
  return Math.round((part / total) * 100);
};

const emptyHours = () =>
  Array.from({ length: 24 }, (_, hour) => ({
    time: `${String(hour).padStart(2, "0")}:00`,
    trips: 0,
    running: 0,
    alerts: 0,
  }));

export const buildOverviewMetrics = ({
  chartDataStates = {},
  vehicles = [],
  selectedDateRange = "today",
  customDateRange = null,
}) => {
  const tripRows = getRows(chartDataStates.trip_vehicles);
  const activityRows = getRows(chartDataStates.activity_vehicles);
  const runtimeRows = getRows(chartDataStates.runtime_vehicles);
  const overspeedRows = getRows(chartDataStates.overspeed_vehicles);
  const haltRows = getRows(chartDataStates.halt_vehicles);
  const haltDevices = getHaltDevices(chartDataStates.halt_vehicles);
  const vehicleList = Array.isArray(vehicles)
    ? vehicles
    : vehicles?.data || vehicles?.vehicles || [];

  const completedTrips = tripRows.filter((row) => isOnTimeTrip(row.status)).length;
  const delayedTrips = tripRows.filter((row) => isDelayedTrip(row.status)).length;
  const scoredTrips = completedTrips + delayedTrips;
  const tripCount = tripRows.length;

  const speeds = activityRows
    .map((row) => toNumber(row.averageSpeed))
    .filter((speed) => speed > 0);
  const avgSpeed =
    speeds.length > 0
      ? speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length
      : 0;

  const distance = runtimeRows.reduce(
    (sum, row) => sum + toNumber(row.noOfKmsCovered),
    0,
  );
  const deviations = activityRows.reduce(
    (sum, row) => sum + (parseInt(row.outOfRouteCount, 10) || 0),
    0,
  );

  const expectedHours = getExpectedHours(selectedDateRange, customDateRange);
  const runtimeByVehicle = new Map();
  runtimeRows.forEach((row) => {
    const key =
      row.deviceId || row.device_id || row.vehicleNumber || row.vehicleName;
    if (!key) return;
    runtimeByVehicle.set(
      key,
      (runtimeByVehicle.get(key) || 0) + toNumber(row.totalMovingHours),
    );
  });

  const utilizationBuckets = {
    "High (80 - 100%)": 0,
    "Medium (50 - 80%)": 0,
    "Low (20 - 50%)": 0,
    "Idle (< 20%)": 0,
  };

  const utilizationSources =
    runtimeByVehicle.size > 0
      ? Array.from(runtimeByVehicle.values())
      : vehicleList.map(() => 0);

  utilizationSources.forEach((hours) => {
    const ratio = Math.min(hours / expectedHours, 1);
    if (ratio >= 0.8) utilizationBuckets["High (80 - 100%)"] += 1;
    else if (ratio >= 0.5) utilizationBuckets["Medium (50 - 80%)"] += 1;
    else if (ratio >= 0.2) utilizationBuckets["Low (20 - 50%)"] += 1;
    else utilizationBuckets["Idle (< 20%)"] += 1;
  });

  const utilizedCount =
    utilizationBuckets["High (80 - 100%)"] +
    utilizationBuckets["Medium (50 - 80%)"];
  const utilizationTotal = utilizationSources.length;
  const fleetUtilization = percent(utilizedCount, utilizationTotal);

  const healthCounts = { Healthy: 0, Warning: 0, Critical: 0, Inactive: 0 };
  vehicleList.forEach((vehicle) => {
    const status = getVehicleStatus(vehicle);
    if (status === "DEAD" || status === "CRITICAL") healthCounts.Critical += 1;
    else if (status === "INACTIVE" || status === "WARNING") healthCounts.Warning += 1;
    else if (!status || status === "OFFLINE") healthCounts.Inactive += 1;
    else healthCounts.Healthy += 1;
  });

  const healthTotal = vehicleList.length;
  const health = Object.entries(healthCounts).map(([name, count]) => ({
    name,
    count,
    value: percent(count, healthTotal),
    color: HEALTH_COLORS[name],
  }));

  const activity = emptyHours();
  const runningByHour = Array.from({ length: 24 }, () => new Set());

  tripRows.forEach((row) => {
    const hour = parseHour(row.startTime || row.date || row.timestamp);
    if (hour == null) return;
    activity[hour].trips += 1;
    const vehicleKey = row.vehicleName || row.vehicleNumber || row.deviceId;
    if (vehicleKey) runningByHour[hour].add(vehicleKey);
  });

  activityRows.forEach((row) => {
    const hour = parseHour(row.timestamp || row.time || row.date);
    const vehicleKey = row.vehicleName || row.vehicleNumber || row.deviceId;
    if (hour == null || !vehicleKey) return;
    if (toNumber(row.averageSpeed) > 0 || toNumber(row.totalMovingHours) > 0) {
      runningByHour[hour].add(vehicleKey);
    }
  });

  overspeedRows.forEach((row) => {
    const hour = parseHour(row.timestamp || row.time || row.createdAt);
    if (hour != null) activity[hour].alerts += 1;
  });

  activity.forEach((slot, hour) => {
    slot.running = runningByHour[hour].size;
  });

  const routeCounts = tripRows.reduce((acc, row) => {
    const name = row.routeName || row.route || row.routeId || "Unknown Route";
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const routes = Object.entries(routeCounts)
    .map(([name, trips]) => ({ name, trips }))
    .sort((a, b) => b.trips - a.trips);

  const utilization = Object.entries(utilizationBuckets).map(([name, value]) => ({
    name,
    value,
    color: UTILIZATION_COLORS[name],
    percent: percent(value, utilizationTotal),
  }));

  let inactiveHalts = 0;
  let deadHalts = 0;
  if (haltDevices.length) {
    haltDevices.forEach((device) => {
      inactiveHalts += parseInt(device.totalInactivePeriods || device.inactivePeriods || 0, 10) || 0;
      deadHalts += parseInt(device.totalDeadPeriods || device.deadPeriods || 0, 10) || 0;
    });
  } else {
    haltRows.forEach((row) => {
      const haltType = String(row.type || row.haltType || "").toLowerCase();
      if (haltType.includes("inactive")) inactiveHalts += 1;
      else if (haltType.includes("dead")) deadHalts += 1;
    });
  }

  const incidents = [
    ["Overspeed", overspeedRows.length],
    ["Route Deviations", deviations],
    ["Inactive Vehicles", healthCounts.Warning + healthCounts.Inactive],
    ["Inactive Halts", inactiveHalts],
    ["Dead Halts", deadHalts],
  ];

  return {
    cards: [
      {
        title: "Trips",
        value: tripCount,
        iconKey: "trips",
        tone: "blue",
        dataKey: "trip_vehicles",
      },
      {
        title: "On Time Performance",
        value: `${percent(completedTrips, scoredTrips)}%`,
        iconKey: "ontime",
        tone: "green",
        dataKey: "trip_vehicles",
      },
      {
        title: "Avg. Fleet Speed",
        value: `${avgSpeed.toFixed(0)} km/h`,
        iconKey: "speed",
        tone: "amber",
        dataKey: "activity_vehicles",
      },
      {
        title: "Overspeed Incidents",
        value: overspeedRows.length,
        iconKey: "overspeed",
        tone: "red",
        dataKey: "overspeed_vehicles",
      },
      {
        title: "Distance Covered",
        value: `${Math.round(distance)} km`,
        iconKey: "distance",
        tone: "purple",
        dataKey: "runtime_vehicles",
      },
      {
        title: "Fleet Utilization",
        value: `${fleetUtilization}%`,
        iconKey: "utilization",
        tone: "cyan",
        dataKey: "runtime_vehicles",
      },
    ],
    activity,
    health,
    healthyPercent: health.find((item) => item.name === "Healthy")?.value || 0,
    routes,
    utilization,
    fleetUtilization,
    hourly: activity.map(({ time, trips }) => ({ time, trips })),
    incidents,
  };
};
